import type {
  EffectResource,
  MutatorResource,
} from '@/features/legends/api/resources/effects'
import type { LegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import {
  mapUnknownResourceToElement,
  UnknownElementError,
} from '@/features/legends/editor/legend-document'
import type { Effect, Mutator } from './effects'

/**
 * Maps an effect, fixing references to legacy question IDs. The fields from
 * before mutators are left out.
 *
 * @param resource
 * @param ids
 */
export function mapEffectResourceToEffect(
  resource: EffectResource,
  ids: LegacyIdFixer,
): Effect {
  return {
    mutators: resource.mutators?.map((mutator) =>
      mapMutatorResourceToMutator(mutator, ids),
    ),
  }
}

/**
 * Maps an effect to the API's.
 *
 * @param effect
 */
export function mapEffectToResource(effect: Effect): EffectResource {
  return { mutators: effect.mutators?.map(mapMutatorToResource) }
}

function mapMutatorResourceToMutator(
  resource: MutatorResource,
  ids: LegacyIdFixer,
): Mutator {
  const targets = resource.targets?.map((target) => ids.resolve(target))
  switch (resource.type) {
    case 'ClearInventoryItemQuantity':
      return { type: 'ClearInventoryItemQuantity', targets }
    case 'CopyInventoryItem':
      return {
        type: 'CopyInventoryItem',
        targets,
        source: ids.resolve(resource.source),
      }
    case 'InventoryItem':
      return {
        type: 'InventoryItem',
        targets,
        op: resource.op,
        quantity: resource.quantity,
        properties: resource.properties && {
          quantity: resource.properties.quantity,
          courtesyMultiplier: resource.properties.courtesyMultiplier,
        },
      }
    case 'List':
      return {
        type: 'List',
        targets,
        op: resource.op,
        expr: resource.expr,
        multi: resource.multi,
      }
    case 'Replace':
      return { type: 'Replace', targets, expr: resource.expr }
    case 'Vary':
      return { type: 'Vary', targets, op: resource.op, expr: resource.expr }
  }
  return mapUnknownResourceToElement(resource)
}

function mapMutatorToResource(mutator: Mutator): MutatorResource {
  switch (mutator.type) {
    case 'ClearInventoryItemQuantity':
      return { type: 'ClearInventoryItemQuantity', targets: mutator.targets }
    case 'CopyInventoryItem':
      return {
        type: 'CopyInventoryItem',
        targets: mutator.targets,
        source: mutator.source,
      }
    case 'InventoryItem':
      return {
        type: 'InventoryItem',
        targets: mutator.targets,
        op: mutator.op,
        properties: mutator.properties && {
          quantity: mutator.properties.quantity,
          courtesyMultiplier: mutator.properties.courtesyMultiplier,
        },
        quantity: mutator.quantity,
      }
    case 'List':
      return {
        type: 'List',
        targets: mutator.targets,
        op: mutator.op,
        expr: mutator.expr,
        multi: mutator.multi,
      }
    case 'Replace':
      return { type: 'Replace', targets: mutator.targets, expr: mutator.expr }
    case 'Vary':
      return {
        type: 'Vary',
        targets: mutator.targets,
        op: mutator.op,
        expr: mutator.expr,
      }
    case 'Unknown':
      throw new UnknownElementError('mutator', mutator)
  }
}
